import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import './GameLeaderboardsPage.css';
import { ClompApi } from '../api/ClompApi';
import { formatStat } from '../components/solarflareStats';

const api = new ClompApi();

// The boards for each game, all filled from the backend by players who have signed the game in to their account.
// Solarflare's Player stats board shows one of its counters at a time (picked like Space Jump's levels); its
// speedruns will be filled once it reports runs, and until then show their empty state. `empty` is what a board says
// when nobody is on it (a board's own `empty` wins).
const GAMES = [
    {
        id: 'solarflare',
        name: 'Solarflare',
        empty: 'No Solarflare runs have been recorded yet. Check back soon!',
        boards: [
            { id: 'stats', name: 'Player stats', value: 'Stat', empty: 'Nobody is on this board yet. Sign in with clomp in Solarflare to get your stats on it!' },
            { id: 'speedrun', name: 'Speedruns', value: 'Time' }
        ]
    },
    {
        id: 'spacejump',
        name: 'Space Jump',
        empty: 'No Space Jump runs have been recorded yet. Check back soon!',
        boards: [
            { id: 'fastest', name: 'Fastest clear', value: 'Time' },
            { id: 'jumps', name: 'Fewest jumps', value: 'Jumps' }
        ]
    },
    {
        id: 'clompyclicker',
        name: 'Clompy Clicker',
        empty: 'Nobody has collected any diamonds yet. Be the first!',
        boards: [
            { id: 'diamonds', name: 'All-time diamonds', value: 'Diamonds' }
        ]
    }
];

// The game a link asked for with ?game=<id> (as the Clompy Clicker game's Leaderboard button does), or the first
function gameFromAddress(search) {
    let wanted = new URLSearchParams(search).get('game');
    return GAMES.find((game) => game.id === wanted) || GAMES[0];
}

export class GameLeaderboardsPage extends React.Component {

    state = {
        game: gameFromAddress(this.props.location.search).id,
        board: gameFromAddress(this.props.location.search).boards[0].id,
        // Space Jump: the reply from GET /spacejump/leaderboard, and which of its boards (full run or a level) is shown
        spaceJump: null,
        spaceJumpLevel: 'run',
        // Clompy Clicker: the players who have earned the most diamonds of all time, from GET /clicker/leaderboard
        clicker: [],
        // Solarflare: its boards ([{ id, name, unit }], from GET /solarflare/leaderboard), which counter the Player
        // stats board shows, and the entries loaded so far for each board id
        solarflareBoards: [],
        solarflareStat: 'play_s',
        solarflare: {}
    };

    componentDidMount() {
        api.games.getSpaceJumpLeaderboard().then((data) => {
            this.setState({ spaceJump: data, spaceJumpLevel: data.run });
        }).catch((err) => {
            // Leave the boards on their empty state
            console.log(err);
        });
        api.games.getClickerLeaderboard().then((data) => {
            this.setState({ clicker: data.entries });
        }).catch((err) => {
            console.log(err);
        });
        // the stat list for the picker comes with any board; the one in view is fetched first
        this.loadSolarflare(this.solarflareBoardID(this.state.board, this.state.solarflareStat));
        if (this.state.board === 'speedrun') {
            this.loadSolarflare('play_s');
        }
    }

    // The backend's id for a Solarflare board: the counter picked for Player stats, else the board's own
    solarflareBoardID(board, stat) {
        return board === 'stats' ? stat : board;
    }

    // Fetch a Solarflare board, unless it has been already (speedruns aren't reported yet, so there is nothing to fetch)
    loadSolarflare(id) {
        if (id === 'speedrun' || this.state.solarflare[id]) {
            return;
        }
        api.games.getSolarflareLeaderboard(id).then((data) => {
            this.setState((state) => ({ solarflareBoards: data.boards, solarflare: { ...state.solarflare, [data.board]: data.entries } }));
        }).catch((err) => {
            // Leave the board on its empty state
            console.log(err);
        });
    }

    // The Solarflare counter a board id names ({ id, name, unit }), or a stand-in until the list has loaded
    solarflareBoard(id) {
        return this.state.solarflareBoards.find((board) => board.id === id) || { id, name: 'Stat', unit: null };
    }

    // Seconds as minutes:seconds.tenths, the way the game shows run times
    formatTime(seconds) {
        let tenths = Math.floor(seconds * 10);
        let wholeSeconds = Math.floor(tenths / 10) % 60;
        return `${Math.floor(tenths / 600)}:${wholeSeconds < 10 ? '0' : ''}${wholeSeconds}.${tenths % 10}`;
    }

    // The rows for a board: [{ id, player, value, date, mods }], already formatted for display; mods are the
    // { id, name } of the game mods that were on for that score
    entriesFor(game, board) {
        if (game.id === 'solarflare') {
            let id = this.solarflareBoardID(board.id, this.state.solarflareStat);
            let unit = this.solarflareBoard(id).unit;
            return (this.state.solarflare[id] || []).map((entry) => ({
                id: entry.id,
                player: entry.player,
                value: formatStat(entry.value, unit),
                date: new Date(entry.date).toLocaleDateString(),
                mods: []
            }));
        }
        if (game.id === 'clompyclicker') {
            return this.state.clicker.map((entry) => ({
                id: entry.id,
                player: entry.player,
                value: entry.value.toLocaleString(),
                date: new Date(entry.date).toLocaleDateString(),
                mods: []
            }));
        }
        if (game.id !== 'spacejump' || !this.state.spaceJump) {
            return [];
        }
        let boards = this.state.spaceJump.boards[this.state.spaceJumpLevel] || {};
        let modNames = this.state.spaceJump.mods || [];
        return (boards[board.id] || []).map((entry) => ({
            id: entry.id,
            player: entry.player,
            value: board.id === 'fastest' ? this.formatTime(entry.value) : entry.value,
            date: new Date(entry.date).toLocaleDateString(),
            mods: modNames.filter((mod) => (entry.mods || []).includes(mod.id))
        }));
    }

    profilePath(entry) {
        return `/profile/${encodeURIComponent(entry.player)}`;
    }

    selectGame(game) {
        this.selectBoard(game, game.boards[0].id);
    }

    selectBoard(game, board) {
        this.setState({ game: game.id, board });
        if (game.id === 'solarflare') {
            this.loadSolarflare(this.solarflareBoardID(board, this.state.solarflareStat));
        }
    }

    selectSolarflareStat(stat) {
        this.setState({ solarflareStat: stat });
        this.loadSolarflare(stat);
    }

    renderBoard(game, board) {
        let entries = this.entriesFor(game, board);
        return (
            <div className="glb-board">
                <table className="glb-table">
                    <thead>
                        <tr>
                            <th className="glb-rank">Rank</th>
                            <th>Player</th>
                            <th className="glb-value">{game.id === 'solarflare' && board.id === 'stats' ? this.solarflareBoard(this.state.solarflareStat).name : board.value}</th>
                            <th className="glb-date">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, i) => (
                            // the whole row opens the player's profile; the name is the link for keyboards and screen readers
                            <tr key={entry.id} className="glb-row" onClick={() => this.props.history.push(this.profilePath(entry))}>
                                <td className="glb-rank">{i + 1}</td>
                                <td>
                                    <Link className="glb-player" to={this.profilePath(entry)} onClick={(e) => e.stopPropagation()}>{entry.player}</Link>
                                    {(entry.mods || []).map((mod) => (
                                        <img key={mod.id} className="glb-mod" src={`/img/spacejump/mods/${mod.id}.svg`}
                                            alt={mod.name} title={`${mod.name} mod`} />
                                    ))}
                                </td>
                                <td className="glb-value">{entry.value}</td>
                                <td className="glb-date">{entry.date}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {entries.length === 0 ?
                    <p className="glb-empty">{board.empty || game.empty}</p>
                    : null}
            </div>
        );
    }

    render() {
        let game = GAMES.find((g) => g.id === this.state.game);
        let board = game.boards.find((b) => b.id === this.state.board);

        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className={`glb-page glb-${game.id}`}>
                    <div className="glb-page-header">
                        <p className="glb-eyebrow">Clomp games</p>
                        <h1>Leaderboard</h1>
                        <p className="glb-intro">Speedruns, stats and high scores from Clomp players.</p>
                    </div>
                    <div className="glb-tabs" role="tablist" aria-label="Game">
                        {GAMES.map((g) => (
                            <button key={g.id} type="button" role="tab" aria-selected={g.id === game.id}
                                className={g.id === game.id ? 'is-active' : ''} onClick={() => this.selectGame(g)}>
                                {g.name}
                            </button>
                        ))}
                    </div>
                    <div className="glb-boards" aria-label="Board">
                        {game.boards.map((b) => (
                            <button key={b.id} type="button" aria-pressed={b.id === board.id}
                                className={b.id === board.id ? 'is-active' : ''} onClick={() => this.selectBoard(game, b.id)}>
                                {b.name}
                            </button>
                        ))}
                    </div>
                    {game.id === 'spacejump' && this.state.spaceJump ?
                        <label className="glb-level">
                            Level
                            <select value={this.state.spaceJumpLevel} onChange={(e) => this.setState({ spaceJumpLevel: e.target.value })}>
                                <option value={this.state.spaceJump.run}>Full run (all 10 levels)</option>
                                {this.state.spaceJump.levels.map((level, i) => (
                                    <option key={level.id} value={level.id}>{i + 1}. {level.name}</option>
                                ))}
                            </select>
                        </label>
                        : null}
                    {game.id === 'solarflare' && board.id === 'stats' && this.state.solarflareBoards.length > 0 ?
                        <label className="glb-level">
                            Stat
                            <select value={this.state.solarflareStat} onChange={(e) => this.selectSolarflareStat(e.target.value)}>
                                {this.state.solarflareBoards.map((b) => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </label>
                        : null}
                    {this.renderBoard(game, board)}
                </div>
            </>
        );
    }

}
